import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchKpiCountsApi, fetchKpiInsightApi } from "../../api/KpiReportingApi";

import { KPI_REPORTING_FILTERS } from "../../constants/KPIReportingFiltersConstants";
import { previousQuarterForCurrentYear } from "../../utils/DateUtility";

type filterType = {
    startDate: Date;
    endDate: Date;
    businessEntity: string;
    operatingAsset: string;
    siteName: string[];
    selectedKpiName: string | null;
}

type KpiReportingType = {
    kpiCountLoading: boolean | null;
    kpiCount: any;
    kpiCountError: string;

    kpiInsightLoading: boolean | null;
    kpiInsight: any;
    kpiInsightError: string;

    filtersApplied: filterType;
}

const defaultFilterDates = previousQuarterForCurrentYear();
const initialState: KpiReportingType = {
    kpiCountLoading: null,
    kpiCount: null,
    kpiCountError: '',

    kpiInsightLoading: null,
    kpiInsight: {
        'LOPC': null,
        'PCIT': null,
        'SOLE': null,
        'DSS': null,
    },
    kpiInsightError: '',

    filtersApplied: {
        startDate: defaultFilterDates.startDate,
        endDate: defaultFilterDates.endDate,
        businessEntity: 'all',
        operatingAsset: 'all',
        siteName: [...KPI_REPORTING_FILTERS.SITE_NAMES_ASR, ...KPI_REPORTING_FILTERS.SITE_NAMES_JV],
        selectedKpiName: null
    }
}

const kpiReportingSlice = createSlice({
    name: 'kpi_report',
    initialState: initialState,
    reducers: {
        applyFilters: (state, action) => {
            state.filtersApplied = { ...action.payload };
            state.kpiInsight = {
                'LOPC': null,
                'PCIT': null,
                'SOLE': null,
                'DSS': null,
            }
        }
    },
    extraReducers: builder => {
        builder
            // get kpi count
            .addCase(getKpiCount.pending, (state) => {
                state.kpiCountLoading = true;
            })
            .addCase(getKpiCount.fulfilled, (state, action) => {
                state.kpiCountLoading = false;
                state.kpiCount = { ...action.payload }
            })
            .addCase(getKpiCount.rejected, (state) => {
                state.kpiCountLoading = false;
                state.kpiCountError = "Unable to get kpi count.";
            })

            // get kpi insight
            .addCase(getKpiInsight.pending, (state) => {
                state.kpiInsightLoading = true;
            })
            .addCase(getKpiInsight.fulfilled, (state, action) => {
                state.kpiInsightLoading = false;

                const selectedKpiName = state.filtersApplied.selectedKpiName;
                state.kpiInsight[selectedKpiName!] = { ...action.payload };
            })
            .addCase(getKpiInsight.rejected, (state) => {
                const selectedKpiName = state.filtersApplied.selectedKpiName;
                selectedKpiName && (state.kpiInsight[selectedKpiName] = null)

                state.kpiInsightLoading = false;
                state.kpiInsightError = "Unable to get kpi insight.";
            })
    }
});


export const getKpiCount = createAsyncThunk(
    "kpi_report/getKpiCount",
    async (payload: any) => {
        return await fetchKpiCountsApi(payload);
    }
)

export const getKpiInsight = createAsyncThunk(
    "kpi_report/getKpiInsight",
    async (payload: any) => {
        return await fetchKpiInsightApi(payload);
    }
)


export const { applyFilters } = kpiReportingSlice.actions;
export default kpiReportingSlice.reducer;

export const selectIfKpiCountLoading = (state: any) => state.kpi_report.kpiCountLoading;
export const selectKpiCount = (state: any) => state.kpi_report.kpiCount;

export const selectIfKpiInsightLoading = (state: any) => state.kpi_report.kpiInsightLoading;
export const selectKpiInsight = (state: any) => state.kpi_report.kpiInsight;

export const selectKpiFiltersApplied = (state: any) => state.kpi_report.filtersApplied;